import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { CaretDown, Check, X } from '@phosphor-icons/react';

import { ApiError } from '@/api/client';
import {
  createAndLinkGuardian,
  linkGuardian,
  listMembers,
  type GuardianRelationship,
} from '@/api/members';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SearchInput } from '@/components/ui/search-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

// AddGuardianModal handles two flows for attaching a guardian to a
// member from the MemberCard's Relations tab:
//
//   1. "From members" — picker over existing org members. Click someone,
//      pick a relationship, save. Server links via member_guardian.
//   2. "Add new" — full member-create form (name + optional contact +
//      optional address + personal_id) plus a relationship. Server
//      atomically inserts the member AND creates the guardian link.
//
// Mirrors the AdminFormModal two-tab pattern. Uses the native <dialog>
// element so focus-trap, escape-to-close, and backdrop click are
// browser-handled — no library, no portal-rolled-our-own dance.

export interface AddGuardianModalProps {
  open: boolean;
  onClose: () => void;
  slug: string;
  memberID: string;
  // Display name of the dependent member, shown in the modal title so
  // the admin always knows who they're attaching the guardian to.
  memberDisplayName: string;
}

type Tab = 'member' | 'new';

const RELATIONSHIPS: GuardianRelationship[] = [
  'parent',
  'grandparent',
  'sibling',
  'other_relative',
  'legal_guardian',
  'other',
];

export function AddGuardianModal({
  open,
  onClose,
  slug,
  memberID,
  memberDisplayName,
}: AddGuardianModalProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);

  const [tab, setTab] = useState<Tab>('member');
  const [relationship, setRelationship] = useState<GuardianRelationship>('parent');
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // "From members" tab state. We keep the whole picked member (not just the
  // id) so the combobox trigger can show the name even after the search box
  // is re-typed and the candidate list no longer contains the selection.
  const [memberSearch, setMemberSearch] = useState('');
  const [pickedMember, setPickedMember] = useState<{ id: string; name: string } | null>(null);

  // "Add new" tab state — full member-create fields.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [personalId, setPersonalId] = useState('');

  // Reset everything on open. Pre-existing modal instances wouldn't keep
  // half-filled state from a previous member's flow.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      setTab('member');
      setRelationship('parent');
      setMemberSearch('');
      setPickedMember(null);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setStreetAddress('');
      setPostalCode('');
      setCity('');
      setPersonalId('');
      setErrorCode(null);
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const handleClose = () => onClose();
    el.addEventListener('close', handleClose);
    return () => el.removeEventListener('close', handleClose);
  }, [onClose]);

  // Members search — only loaded when the picker tab is active. 250ms
  // debounce would be marginal here since the underlying ListMembers
  // call is fast; skipping for v1 in favour of a smaller surface.
  const membersQuery = useQuery({
    queryKey: ['members', slug, memberSearch, 'add-guardian'],
    queryFn: () => listMembers(token, slug, { search: memberSearch, limit: 25, includeGuardians: true }),
    enabled: !!token && !!slug && open && tab === 'member',
  });
  // Candidate guardians to pick from. Filter out:
  //   - the dependent themselves (can't be your own guardian);
  //   - members surfaced only because the search matched THEIR guardian, not
  //     their own name/email (matched_via_guardian) — those rows show the
  //     dependent's name, so they'd be confusing in a "pick the guardian" list.
  // Cap the visible list to 8 so the dropdown stays a glanceable size; the
  // server already returns the most-relevant matches first.
  const candidates = (membersQuery.data?.members ?? [])
    .filter((m) => m.id !== memberID && !m.matched_via_guardian)
    .slice(0, 8);

  const linkMut = useMutation({
    mutationFn: () => {
      if (!pickedMember) throw new Error('no member picked');
      return linkGuardian(token, slug, memberID, {
        guardian_member_id: pickedMember.id,
        relationship,
      });
    },
    onSuccess: invalidateAndClose,
    onError: (err) => setErrorCode(err instanceof ApiError ? err.code : 'unknown'),
  });

  const createMut = useMutation({
    mutationFn: () => {
      // Build the payload incrementally so optional empty fields are
      // omitted entirely (TS exactOptionalPropertyTypes — undefined is
      // not the same as missing). Server treats absent + empty
      // identically, so this only affects type-shape.
      const e = email.trim();
      const p = phone.trim();
      const s = streetAddress.trim();
      const pc = postalCode.trim();
      const c = city.trim();
      const pid = personalId.trim();
      return createAndLinkGuardian(token, slug, memberID, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        relationship,
        ...(e && { email: e }),
        ...(p && { phone: p }),
        ...(s && { street_address: s }),
        ...(pc && { postal_code: pc }),
        ...(c && { city: c }),
        ...(pid && { personal_id: pid }),
      });
    },
    onSuccess: invalidateAndClose,
    onError: (err) => setErrorCode(err instanceof ApiError ? err.code : 'unknown'),
  });

  function invalidateAndClose() {
    // Match the keys used by MemberCard's listGuardians / listDependents
    // queries — without a key match the panel won't refetch and the new
    // guardian won't appear until the user closes + reopens the card.
    void queryClient.invalidateQueries({ queryKey: ['guardians', slug, memberID] });
    void queryClient.invalidateQueries({ queryKey: ['dependents', slug, memberID] });
    // The dependent member's has_guardians flag may flip; refresh the
    // members table too so the chevron column re-evaluates.
    void queryClient.invalidateQueries({ queryKey: ['members', slug] });
    onClose();
  }

  const submitting = linkMut.isPending || createMut.isPending;

  const memberPathValid = pickedMember !== null;
  const newPathValid = firstName.trim().length > 0 && lastName.trim().length > 0;
  const formValid = tab === 'member' ? memberPathValid : newPathValid;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!formValid || submitting) return;
    setErrorCode(null);
    if (tab === 'member') {
      linkMut.mutate();
    } else {
      createMut.mutate();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-background p-0 shadow-2xl backdrop:bg-foreground/40"
    >
      <form
        onSubmit={onSubmit}
        noValidate
        className="flex w-[min(40rem,92vw)] flex-col gap-4 p-6"
      >
        <header className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <span className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('memberCard.guardian.modal.eyebrow', {
                name: memberDisplayName,
                defaultValue: 'Add guardian for {{name}}',
              })}
            </span>
            <h2 className="font-heading text-xl font-bold text-foreground">
              {t('memberCard.guardian.modal.title', 'Add guardian')}
            </h2>
          </div>
          <button
            type="button"
            aria-label={t('common.cancel')}
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <div className="flex gap-1 rounded-pill bg-muted p-1 text-sm">
          <TabButton active={tab === 'member'} onClick={() => setTab('member')}>
            {t('memberCard.guardian.modal.tabs.member', 'From members list')}
          </TabButton>
          <TabButton active={tab === 'new'} onClick={() => setTab('new')}>
            {t('memberCard.guardian.modal.tabs.new', 'Add new person')}
          </TabButton>
        </div>

        {tab === 'member' && (
          <MemberCombobox
            search={memberSearch}
            onSearchChange={setMemberSearch}
            members={candidates}
            loading={membersQuery.isLoading}
            picked={pickedMember}
            onPick={setPickedMember}
            container={dialogRef.current}
          />
        )}

        {tab === 'new' && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t('memberCard.guardian.modal.fields.firstName', 'First name')} required>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                maxLength={120}
                autoComplete="given-name"
                autoFocus
              />
            </Field>
            <Field label={t('memberCard.guardian.modal.fields.lastName', 'Last name')} required>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                maxLength={120}
                autoComplete="family-name"
              />
            </Field>
            <Field label={t('memberCard.guardian.modal.fields.email', 'Email')}>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={320}
                autoComplete="email"
              />
            </Field>
            <Field label={t('memberCard.guardian.modal.fields.phone', 'Phone')}>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                maxLength={40}
                autoComplete="tel"
              />
            </Field>
            <Field
              className="sm:col-span-2"
              label={t('memberCard.guardian.modal.fields.streetAddress', 'Street address')}
            >
              <Input
                value={streetAddress}
                onChange={(e) => setStreetAddress(e.target.value)}
                maxLength={200}
                autoComplete="street-address"
              />
            </Field>
            <Field label={t('memberCard.guardian.modal.fields.postalCode', 'Postal code')}>
              <Input
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                maxLength={20}
                autoComplete="postal-code"
              />
            </Field>
            <Field label={t('memberCard.guardian.modal.fields.city', 'City')}>
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                maxLength={120}
                autoComplete="address-level2"
              />
            </Field>
            <Field
              className="sm:col-span-2"
              label={t('memberCard.guardian.modal.fields.personalId', 'Personal ID')}
              hint={t(
                'memberCard.guardian.modal.fields.personalIdHint',
                'Optional. Server validates against the org country.',
              )}
            >
              <Input
                value={personalId}
                onChange={(e) => setPersonalId(e.target.value)}
                maxLength={40}
                spellCheck={false}
              />
            </Field>
          </div>
        )}

        {/* Relationship picker — same on both tabs, sits right above
            the action footer so it's the last decision before save. */}
        <div className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          <span>{t('memberCard.guardian.modal.fields.relationship', 'Relationship')}</span>
          <Select
            value={relationship}
            onValueChange={(v) => setRelationship(v as GuardianRelationship)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            {/* Portal into the native <dialog> — showModal() puts the dialog
                in the browser's top layer, so a dropdown portaled to
                document.body (Radix's default) would render BEHIND it. */}
            <SelectContent container={dialogRef.current}>
              {RELATIONSHIPS.map((r) => (
                <SelectItem key={r} value={r}>
                  {t(`members.guardians.relationship.${r}`, { defaultValue: r })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {errorCode && (
          <p role="alert" className="text-sm text-destructive">
            {t(`memberCard.guardian.modal.error.${errorCode}`, {
              defaultValue: t('memberCard.guardian.modal.error.unknown'),
            })}
          </p>
        )}

        <footer className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!formValid || submitting}>
            {submitting
              ? t('memberCard.guardian.modal.saving', 'Saving…')
              : t('memberCard.guardian.modal.save', 'Add guardian')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex-1 rounded-pill px-3 py-1.5 text-sm font-semibold transition-colors',
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function Field({
  label,
  hint,
  className,
  required,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={cn('flex flex-col gap-1.5 text-sm font-medium text-foreground', className)}>
      <span>
        {label}
        {required && <span className="ml-1 text-destructive">*</span>}
      </span>
      {children}
      {hint && <span className="text-xs font-normal text-muted-foreground">{hint}</span>}
    </label>
  );
}

// MemberCombobox — a searchable member picker. The trigger shows the picked
// member (or a placeholder); opening it reveals a search box + the filtered
// candidate list.
//
// Built on the design-system Popover (Radix Popper), portaled into the native
// <dialog> via `container` so it shares showModal()'s top layer (a
// document.body portal would paint BEHIND the dialog). Because Popper anchors
// the panel and does collision handling, the list can extend BEYOND the
// dialog's box — it grows to fill the available viewport height
// (--radix-popover-content-available-height) and flips/shifts to stay
// on-screen, instead of scrolling inside a fixed-height box clipped to the
// dialog.
function MemberCombobox({
  search,
  onSearchChange,
  members,
  loading,
  picked,
  onPick,
  container,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  members: Array<{ id: string; first_name: string; last_name: string; email: string }>;
  loading: boolean;
  picked: { id: string; name: string } | null;
  onPick: (m: { id: string; name: string } | null) => void;
  container: HTMLElement | null;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
      <span>{t('memberCard.guardian.modal.fields.member', 'Member')}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-haspopup="listbox"
            className={cn(
              'flex min-h-9 w-full items-center justify-between gap-2 rounded-pill border border-border bg-background px-3 py-1.5 text-sm font-medium transition-colors',
              'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              picked ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            <span className="line-clamp-1 text-left">
              {picked
                ? picked.name
                : t('memberCard.guardian.modal.fields.memberPlaceholder', 'Select a member')}
            </span>
            <CaretDown className="h-4 w-4 shrink-0 opacity-60" weight="bold" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent
          container={container}
          align="start"
          sideOffset={6}
          // Focus the search box on open rather than the panel itself.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            searchRef.current?.focus();
          }}
          className="flex max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden rounded-xl border border-border bg-popover p-0 shadow-lg"
        >
          <div className="p-2">
            <SearchInput
              ref={searchRef}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t(
                'memberCard.guardian.modal.fields.memberSearchPlaceholder',
                'Search by name or email…',
              )}
              clearLabel={t('common.clearSearch')}
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto border-t border-border">
            {loading && (
              <p className="px-3 py-2 text-sm text-muted-foreground">{t('common.loading')}</p>
            )}
            {!loading && members.length === 0 && (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                {t(
                  'memberCard.guardian.modal.memberEmpty',
                  'No members match. Try a different search.',
                )}
              </p>
            )}
            {members.map((m) => {
              const fullName = `${m.first_name} ${m.last_name}`.trim();
              const isPicked = picked?.id === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => {
                    onPick({ id: m.id, name: fullName });
                    setOpen(false);
                  }}
                  aria-pressed={isPicked}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors',
                    isPicked ? 'bg-primary/10' : 'hover:bg-muted/50',
                  )}
                >
                  <span className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">{fullName}</span>
                    <span className="text-xs text-muted-foreground">{m.email || '—'}</span>
                  </span>
                  {isPicked && (
                    <Check className="h-4 w-4 shrink-0 text-primary" weight="bold" aria-hidden />
                  )}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
